import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslocoService } from '@jsverse/transloco';
import { SkWhatsNewService } from '@mercadoni/stark-ui';
import { firstValueFrom } from 'rxjs';

import type { FuryRelease } from '../core/interfaces';
import { AuthService } from '../core/services/auth/auth.service';
import { FirestoreWhatsNewStorage } from './firestore-whats-new-storage.service';
import { ONBOARDING_RELEASE_SCOPES } from './onboarding-i18n';
import { type ReleaseViewer, visibleReleases } from './release-visibility';

/**
 * Decides which releases the current user may see and hands exactly that list to the stark
 * engine with `setCatalog()`. Because the engine only ever knows the visible list, its auto-open
 * queue, `unseenCount` (bell badge) and the drawer feed agree by construction, and releases the
 * user is not eligible for are never displayed — hence never marked seen.
 *
 * The engine is configured without a `releases` source on purpose: its catalog stays "not loaded"
 * until this service has the user's roles, tenant and first-visit state, so the one-shot auto-open
 * can never run against an unfiltered catalog.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewVisibilityService {
  private readonly auth = inject(AuthService);
  private readonly storage = inject(FirestoreWhatsNewStorage);
  private readonly engine = inject(SkWhatsNewService);
  private readonly transloco = inject(TranslocoService);

  /** Active language; releases and tour steps hold final text, so they are rebuilt when it changes. */
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  private readonly releasesModule = signal<typeof import('./releases') | null>(null);
  private buildToken = 0;
  /** Language the current `allSignal` content was built for; part of the applied-catalog key. */
  private builtLang = '';

  private readonly allSignal = signal<FuryRelease[] | null>(null);
  private readonly previewing = signal(false);
  /** True while the catalogs of a newly selected language load; no release can be opened then. */
  private readonly switching = signal(false);
  private readonly loadErrorSignal = signal<string | null>(null);
  /** Set when the onboarding catalogs could not be loaded; cleared by a successful (re)load. */
  readonly loadError = this.loadErrorSignal.asReadonly();

  /** Dev preview: plays the intro even though the engine's own intro already ran this session. */
  readonly forcedIntro = signal(false);
  /** First-visit status latched for the session, so finishing the welcome doesn't reshuffle the catalog. */
  private readonly firstVisitLatched = signal<boolean | null>(null);
  private appliedKey: string | null = null;

  /** Every release in the code catalog in the active language, or `null` until loaded. */
  readonly allReleases = this.allSignal.asReadonly();

  /** Roles and tenant used for audience matching. */
  readonly viewer = computed<ReleaseViewer>(() => {
    const mapped = this.auth.mappedRoles();
    return {
      roles: mapped.length > 0 ? mapped : [this.auth.userRole()],
      clientId: this.auth.clientId(),
    };
  });

  /** Releases visible to the current user, or `null` while roles, tenant or first-visit state are unsettled. */
  readonly visible = computed<FuryRelease[] | null>(() => {
    const all = this.allSignal();
    const firstVisit = this.firstVisitLatched();
    if (!all || firstVisit === null || !this.auth.user()) return null;
    return visibleReleases(all, this.viewer(), Date.now(), firstVisit);
  });

  constructor() {
    void import('./releases').then((m) => this.releasesModule.set(m));

    // The release data is plain text, so it is built only after the catalogs of the active
    // language are loaded, and again whenever the language changes.
    effect(() => {
      const module = this.releasesModule();
      const lang = this.lang();
      if (!module) return;
      untracked(() => void this.rebuildReleases(module, lang));
    });

    effect(() => {
      const exists = this.storage.documentExists();
      untracked(() => {
        if (exists === null) return;
        this.firstVisitLatched.update((latched) => latched ?? !exists);
      });
    });

    effect(() => {
      const visible = this.visible();
      const previewing = this.previewing();
      const switching = this.switching();
      untracked(() => {
        if (previewing) return;
        if (switching) {
          // Session text is captured when a release opens, so nothing may open in the old language
          // while the new one is loading: the engine gets an empty catalog until it is ready.
          if (this.appliedKey !== null) this.engine.setCatalog([]);
          this.appliedKey = null;
          return;
        }
        if (visible === null) return;
        const key = `${this.builtLang}:${visible.map((release) => release.id).join('|')}`;
        if (key === this.appliedKey) return;
        this.appliedKey = key;
        this.engine.setCatalog(visible);
      });
    });
  }

  /** Retries loading the onboarding catalogs after a failure (no-op while they are loaded). */
  retryLoad(): void {
    const module = this.releasesModule();
    if (module && this.loadErrorSignal() !== null) void this.rebuildReleases(module, this.lang());
  }

  private async rebuildReleases(module: typeof import('./releases'), lang: string): Promise<void> {
    const token = ++this.buildToken;
    if (this.allSignal() !== null && this.builtLang !== lang) this.switching.set(true);
    try {
      await Promise.all(
        ONBOARDING_RELEASE_SCOPES.map((scope) =>
          firstValueFrom(this.transloco.load(`${scope}/${lang}`)),
        ),
      );
    } catch (error) {
      if (token !== this.buildToken) return;
      // Never build from catalogs that are missing: that would publish bare keys as release text.
      // The previous catalog (if any) stays; with none, no release is offered until a retry works.
      this.loadErrorSignal.set(error instanceof Error ? error.message : String(error));
      this.appliedKey = null;
      this.switching.set(false);
      return;
    }
    if (token !== this.buildToken) return;
    this.loadErrorSignal.set(null);
    this.builtLang = lang;
    this.allSignal.set(
      module.buildFuryReleases((key, params) => this.transloco.translate(key, params)),
    );
    this.switching.set(false);
  }

  /** Dev preview: replaces the engine catalog with `releases` until `endPreview()`. */
  beginPreview(releases: FuryRelease[]): void {
    this.previewing.set(true);
    this.appliedKey = null;
    this.engine.setCatalog(releases);
  }

  /** Dev preview: restores the engine catalog to the user's visible releases. */
  endPreview(): void {
    this.previewing.set(false);
    this.forcedIntro.set(false);
  }
}
